import { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  type SessionGenerationFormData,
  type SessionTheme,
} from 'src/services/session-generation.service';

export type { SessionGenerationFormData };

const SESSION_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English (en)' },
  { value: 'de', label: 'German (de)' },
  { value: 'tr', label: 'Turkish (tr)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'zh-cn', label: 'Chinese (zh-cn)' },
  { value: 'fr', label: 'French (fr)' },
] as const;

const EMPTY: SessionGenerationFormData = {
  referenceId: '',
  firstname: '',
  lastname: '',
  maidenname: '',
  gender: '',
  birthdate: '',
  addressLine1: '',
  postcode: '',
  city: '',
  country: '',
  redirectUrl: '',
  cardFirstDigits: '',
  cardLastDigits: '',
  theme: '',
  language: 'en',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowName?: string;
  onGenerate: (data: SessionGenerationFormData) => void | Promise<void>;
}

export function SessionGenerationModal({
  open,
  onOpenChange,
  flowName,
  onGenerate,
}: Props) {
  const [data, setData] = useState<SessionGenerationFormData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setData(EMPTY);
      setSubmitting(false);
    }
  }, [open]);

  const update =
    <K extends keyof SessionGenerationFormData>(key: K) =>
    (value: SessionGenerationFormData[K]) =>
      setData(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onGenerate(data);
      setData(EMPTY);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate session</DialogTitle>
          <DialogDescription>
            {flowName ? (
              <>
                Optionally provide applicant data for{' '}
                <span className="font-medium text-foreground">{flowName}</span>.
                All fields are optional — leave blank if not needed for this
                product.
              </>
            ) : (
              'Optionally provide applicant data. All fields are optional — leave blank if not needed for this product.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select
                value={data.theme || undefined}
                onValueChange={v =>
                  update('theme')(
                    v === '__none__' ? '' : (v as SessionTheme),
                  )
                }
              >
                <SelectTrigger id="theme">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select
                value={data.language ?? 'en'}
                onValueChange={v => update('language')(v)}
              >
                <SelectTrigger id="language">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {SESSION_LANGUAGE_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="firstname"
              label="First name"
              value={data.firstname}
              onChange={update('firstname')}
            />
            <Field
              id="lastname"
              label="Last name"
              value={data.lastname}
              onChange={update('lastname')}
            />
            <Field
              id="maidenname"
              label="Maiden name"
              value={data.maidenname}
              onChange={update('maidenname')}
            />
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={data.gender || undefined}
                onValueChange={v => update('gender')(v)}
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">Female (F)</SelectItem>
                  <SelectItem value="M">Male (M)</SelectItem>
                  <SelectItem value="X">Other (X)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              id="birthdate"
              label="Date of birth"
              type="date"
              value={data.birthdate}
              onChange={update('birthdate')}
            />
            <Field
              id="country"
              label="Country (ISO-3)"
              value={data.country}
              onChange={v => update('country')(v.toUpperCase())}
              placeholder="IRL"
              maxLength={3}
            />
            <Field
              id="referenceId"
              label="Reference ID"
              value={data.referenceId}
              onChange={update('referenceId')}
            />
          </div>

          <Field
            id="addressLine1"
            label="Address line 1"
            value={data.addressLine1}
            onChange={update('addressLine1')}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="postcode"
              label="Postcode"
              value={data.postcode}
              onChange={update('postcode')}
            />
            <Field
              id="city"
              label="City"
              value={data.city}
              onChange={update('city')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="cardFirstDigits"
              label="Card first 6 digits"
              value={data.cardFirstDigits}
              onChange={v =>
                update('cardFirstDigits')(v.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="402400"
              maxLength={6}
            />
            <Field
              id="cardLastDigits"
              label="Card last 4 digits"
              value={data.cardLastDigits}
              onChange={v =>
                update('cardLastDigits')(v.replace(/\D/g, '').slice(0, 4))
              }
              placeholder="1703"
              maxLength={4}
            />
          </div>

          <Field
            id="redirectUrl"
            label="Redirect URL"
            type="url"
            value={data.redirectUrl}
            onChange={update('redirectUrl')}
          />

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="text-white">
              {submitting ? 'Generating…' : 'Generate session'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
}: FieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}
